'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import { COLORS } from '@/constants/colors';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQSection from '@/components/FAQSection';
import SupportSection from '@/components/SupportSection';
import TableOfContents from '@/components/TableOfContents';
import CmsContent from '@/components/cms/CmsContent';
import BlogLeadPopup from '@/components/blog/BlogLeadPopup';
import CommentSection from '@/components/blog/CommentSection';
import { useAnalytics } from '@/hooks/useAnalytics';
import { type CmsPost } from '@/lib/cms';
import { type CmsPostWithImageUrl } from '@/app/blog/BlogListPage';

export default function BlogPostPage({
  post,
  heroImageUrl,
  relatedPosts,
}: {
  post: CmsPost;
  heroImageUrl: string;
  relatedPosts: CmsPostWithImageUrl[];
}) {
  const analytics = useAnalytics();
  const articleRef = useRef<HTMLElement>(null);
  const hasTrackedRead = useRef(false);
  const startTime = useRef(Date.now());
  // CMS media occasionally 404s/500s (file missing or broken on the media
  // host). Track the failure in state - rather than mutating the <img>'s
  // style directly in the onError callback - so the fallback (the wrapper's
  // background) only appears via a normal React re-render, never as a raw
  // DOM write racing React's hydration of this node.
  const [heroImageFailed, setHeroImageFailed] = useState(false);

  const blogData = useMemo(
    () => ({
      slug: post.slug,
      title: post.title,
      category: post.category?.title,
      readTime: post.readTime,
      image: heroImageUrl,
    }),
    [post, heroImageUrl]
  );

  useEffect(() => {
    analytics.trackBlogView(blogData);
  }, [analytics, blogData]);

  useEffect(() => {
    if (!articleRef.current || hasTrackedRead.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasTrackedRead.current) {
          hasTrackedRead.current = true;
          const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
          analytics.trackBlogRead({ ...blogData, timeSpent });
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    articleRef.current.appendChild(sentinel);
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (sentinel.parentNode) sentinel.remove();
    };
  }, [analytics, blogData]);

  const faqs = (post.faqs ?? []).map((faq, i) => ({ id: i + 1, ...faq }));

  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>

      <main>
        <Breadcrumbs items={[{ label: 'Blog', href: '/blog' }, { label: post.title }]} />

        <HeroSection>
          <HeroContent>
            <HeroTitle>{post.title}</HeroTitle>
            <HeroIntro>{post.excerpt}</HeroIntro>
            <CTAButtonGroup>
              <CTAButton href="/shop-by-vehicle/">Shop by Vehicle</CTAButton>
              <CTAButtonOutline href="/contact-us">Contact Us</CTAButtonOutline>
            </CTAButtonGroup>
          </HeroContent>
        </HeroSection>

        <ArticleBackground>
          <MainContent>
            <Container>
              <Article ref={articleRef}>
                <ArticleMeta>
                  <AuthorAvatar aria-hidden="true">{(post.author || 'Green Filter').charAt(0)}</AuthorAvatar>
                  <MetaText>
                    <MetaItem $strong>Posted by {post.author || 'Green Filter'}</MetaItem>
                    <MetaSubline>
                      {post.category && <MetaItem>{post.category.title}</MetaItem>}
                      {post.category && post.readTime && <MetaSeparator>·</MetaSeparator>}
                      {post.readTime && <MetaItem>{post.readTime}</MetaItem>}
                    </MetaSubline>
                  </MetaText>
                </ArticleMeta>

                <FeaturedImageWrapper>
                  {!heroImageFailed && (
                    <FeaturedImage
                      src={heroImageUrl}
                      alt={post.heroImage.alt || post.title}
                      fill
                      sizes="(max-width: 768px) 100vw, 800px"
                      priority
                      // CMS serves a pre-sized hero variant; see PostThumbnail.
                      unoptimized
                      onError={() => setHeroImageFailed(true)}
                    />
                  )}
                </FeaturedImageWrapper>

                <TableOfContents containerRef={articleRef} />

                <CmsContent blocks={post.content} />
              </Article>
            </Container>
          </MainContent>
        </ArticleBackground>

        <SupportSection
          text="Need help finding the right filter for your vehicle? Use our vehicle selector or contact our support team for expert assistance."
          primaryButtonText="Shop by Vehicle"
          primaryButtonLink="/shop-by-vehicle"
          secondaryButtonText="Contact Support"
          secondaryButtonLink="/contact-us"
        />

        {faqs.length > 0 && <FAQSection faqs={faqs} title="Frequently Asked Questions" />}

        {relatedPosts.length > 0 && (
          <RelatedSection>
            <Container>
              <RelatedHeading>You Might Also Like</RelatedHeading>
              <RelatedGrid>
                {relatedPosts.map((related) => (
                  <RelatedCard key={related.id} href={`/blog/${related.slug}`}>
                    <RelatedImageWrapper>
                      <RelatedImage
                        src={related.cardImageUrl}
                        alt={related.heroImage.alt || related.title}
                        fill
                        sizes="(max-width: 767px) 100vw, 33vw"
                        unoptimized
                      />
                    </RelatedImageWrapper>
                    <RelatedTitle>{related.title}</RelatedTitle>
                    <RelatedExcerpt>{related.excerpt}</RelatedExcerpt>
                  </RelatedCard>
                ))}
              </RelatedGrid>
            </Container>
          </RelatedSection>
        )}

        <CommentSection postId={post.id} />
      </main>

      <Footer />

      <BlogLeadPopup postSlug={post.slug} />
    </>
  );
}

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 80px 24px;
  text-align: center;
  border-bottom: 1px solid #e9ecef;

  @media (max-width: 767px) {
    padding: 60px 20px;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 24px;
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 32px;
  }
`;

const HeroIntro = styled.p`
  font-family: Lato, sans-serif;
  font-size: 18px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 32px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: ${COLORS.brandGreen};
  color: ${COLORS.white};
  padding: 14px 32px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: ${COLORS.brandGreenDark};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 767px) {
    padding: 12px 28px;
    font-size: 15px;
  }
`;

const CTAButtonOutline = styled(Link)`
  display: inline-block;
  background: transparent;
  color: ${COLORS.brandGreen};
  padding: 14px 32px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  border: 2px solid ${COLORS.brandGreen};
  transition: all 0.3s ease;

  &:hover {
    background: ${COLORS.brandGreen};
    color: ${COLORS.white};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 767px) {
    padding: 12px 28px;
    font-size: 15px;
  }
`;

const CTAButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: center;
  }
`;

// A subtle tinted band behind the article column instead of flat white, so
// the reading area doesn't blend into the page's white background - one of
// the "too empty" fixes.
const ArticleBackground = styled.div`
  background: linear-gradient(180deg, ${COLORS.bgGray} 0%, ${COLORS.white} 320px);
`;

const MainContent = styled.div`
  padding: 48px 24px 80px;

  @media (max-width: 767px) {
    padding: 32px 20px 60px;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Article = styled.article``;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;

  @media (max-width: 767px) {
    margin-bottom: 32px;
  }
`;

const AuthorAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${COLORS.brandGreen};
  color: ${COLORS.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
`;

const MetaText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaSubline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetaItem = styled.span<{ $strong?: boolean }>`
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: ${(props) => (props.$strong ? '#1a1a1a' : '#666')};
  font-weight: ${(props) => (props.$strong ? 600 : 400)};
`;

const MetaSeparator = styled.span`
  color: #ccc;
`;

const FeaturedImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  border-radius: 12px;
  overflow: hidden;
  margin: 24px 0 32px;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
`;

const FeaturedImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RelatedSection = styled.section`
  background: ${COLORS.bgGray};
  padding: 64px 24px;

  @media (max-width: 767px) {
    padding: 48px 20px;
  }
`;

const RelatedHeading = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 32px;
  text-align: center;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled(Link)`
  display: block;
  background: ${COLORS.white};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.25s ease;
  text-decoration: none;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  }
`;

const RelatedImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
`;

const RelatedImage = styled(Image)`
  object-fit: cover;
`;

const RelatedTitle = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 16px 20px 8px;
  line-height: 1.35;
`;

const RelatedExcerpt = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0 20px 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
